import { useMemo } from 'react';

import isNil from 'lodash-es/isNil';
import { PlayIcon } from 'lucide-react';
import { Control, Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { FieldsetCard } from '@/components/atoms/FieldsetCard/FieldsetCard';
import { SectionLayout } from '@/components/atoms/SectionLayout/SectionLayout';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useAccount } from '@/hooks/useAccount';
import useNotificationSounds, {
  DEFAULT_NOTIFICATION_SOUND,
  isNotificationSoundName,
  NotificationSoundName,
} from '@/hooks/useNotificationSounds';
import { usePreferences } from '@/hooks/usePreferences';
import { useRegisterSectionSave } from '@/hooks/useRegisterSectionSave';
import { useResetFormOnAccountSwitch } from '@/hooks/useResetFormOnAccountSwitch';
import { useTrackFormState } from '@/hooks/useTrackFormState';
import useUser from '@/hooks/useUser';
import { getPermissions } from '@/redux/permissions/selectors';
import { trackEvent } from '@/segment/segment';
import { actions } from '@/shared/types/permissions';
import { EventName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { CLEAR_DIRTY_VALUES, KEEP_DIRTY_VALUES } from '@/util/forms/resetOptions';

const FORM_ID = 'profile-notifications-form';

const ACCOUNT_SCOPED_FIELDS = ['weeklyEmail'] as const;

const toBoolean = (value: unknown, fallback: boolean) => (isNil(value) ? fallback : !!value);

type Form = {
  chatSound: boolean;
  notificationSound: NotificationSoundName;
  desktopNotifications: boolean;
  weeklyEmail: boolean;
};

type SwitchFieldName = 'chatSound' | 'desktopNotifications' | 'weeklyEmail';

const SwitchField = ({
  control,
  name,
  label,
  description,
}: {
  control: Control<Form>;
  name: SwitchFieldName;
  label: string;
  description: string;
}) => (
  <Controller
    control={control}
    name={name}
    render={({ field }) => (
      <Switch
        variant="description"
        switchPosition="right"
        label={label}
        description={description}
        checked={field.value}
        onCheckedChange={field.onChange}
      />
    )}
  />
);

export const NotificationsSection = () => {
  const { t } = useTranslation();
  const { user, updateUserAsync } = useUser();
  const { accountUserPrefs, updateAccountUserPreferencesAsync } = usePreferences();
  const { sounds, preview } = useNotificationSounds();
  const { account } = useAccount();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'accounts', actions.UPDATE)
  );

  const rawSound = user?.metadata?.notificationSound;
  const form = useForm<Form>({
    resetOptions: KEEP_DIRTY_VALUES,
    values: {
      chatSound: toBoolean(user?.metadata?.chatSound, true),
      notificationSound: isNotificationSoundName(rawSound) ? rawSound : DEFAULT_NOTIFICATION_SOUND,
      desktopNotifications: toBoolean(user?.metadata?.desktopNotifications, true),
      weeklyEmail: toBoolean(accountUserPrefs?.weekly_email, true),
    },
  });
  const {
    control,
    handleSubmit,
    formState: { isDirty, isSubmitting, dirtyFields },
  } = form;

  useTrackFormState({ isDirty, formId: FORM_ID });
  useResetFormOnAccountSwitch(form, ACCOUNT_SCOPED_FIELDS);

  const chatSound = useWatch({ control, name: 'chatSound' });
  const notificationSound = useWatch({ control, name: 'notificationSound' });

  const soundItems = useMemo(
    () =>
      sounds.map((sound) => (
        <SelectItem key={sound.id} value={sound.id}>
          {sound.name}
        </SelectItem>
      )),
    [sounds]
  );

  const onSubmit = async (data: Form) => {
    const tasks: Promise<unknown>[] = [];
    if (
      user &&
      (dirtyFields.chatSound || dirtyFields.notificationSound || dirtyFields.desktopNotifications)
    ) {
      tasks.push(
        updateUserAsync({
          metadata: {
            ...user.metadata,
            chatSound: data.chatSound,
            notificationSound: data.notificationSound,
            desktopNotifications: data.desktopNotifications,
          },
        })
      );
    }
    if (dirtyFields.weeklyEmail) {
      trackEvent(EventName.ClickEmailNotification, { weekly_email: data.weeklyEmail });
      tasks.push(
        updateAccountUserPreferencesAsync({
          ...accountUserPrefs,
          weekly_email: data.weeklyEmail,
        }).then(() => {
          toast.success(t('notifications.email_updated'));
        })
      );
    }
    await Promise.all(tasks);
    form.reset(data, CLEAR_DIRTY_VALUES);
  };

  useRegisterSectionSave(FORM_ID, isDirty, handleSubmit, onSubmit);

  return (
    <SectionLayout title={t('pages.notifications_simple')}>
      <form id={FORM_ID} onSubmit={handleSubmit(onSubmit)}>
        <FieldsetCard
          isDirty={isDirty}
          isLoading={isSubmitting}
          disabled={!isDirty}
          saveButtonLabel={t('common.save')}
          formId={FORM_ID}
        >
          <div className="space-y-6">
            <div className="space-y-4">
              <SwitchField
                control={control}
                name="chatSound"
                label={t('notifications.messages')}
                description={t('notifications.messages_subtitle')}
              />
              {chatSound && (
                <div className="flex items-center gap-2">
                  <Controller
                    control={control}
                    name="notificationSound"
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger size="sm" className="flex-1">
                          <SelectValue
                            placeholder={t('notifications.notification_sound_placeholder')}
                          />
                        </SelectTrigger>
                        <SelectContent>{soundItems}</SelectContent>
                      </Select>
                    )}
                  />
                  <Button
                    type="button"
                    size="icon-md"
                    variant="outline-neutral"
                    onClick={() => preview(notificationSound)}
                    aria-label={t('notifications.notification_sound_play')}
                  >
                    <PlayIcon className="size-4" />
                  </Button>
                </div>
              )}
            </div>

            <SwitchField
              control={control}
              name="desktopNotifications"
              label={t('notifications.desktop_notifications')}
              description={t('notifications.desktop_notifications_subtitle')}
            />

            {canWrite && (
              <SwitchField
                control={control}
                name="weeklyEmail"
                label={t('notifications.weekly_email')}
                description={t('notifications.weekly_email_subtitle', { 0: account?.name })}
              />
            )}
          </div>
        </FieldsetCard>
      </form>
    </SectionLayout>
  );
};
